import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../admin/AdminLayout';
import { apiFetch } from '../../services/apiClient';
import GlancePillows from '../admin/GlancePillows';
import ActivityPills from '../../components/ActivityPills';
import MorningBriefs from '../../components/MorningBriefs';
import ProjectsTable from '../admin/ProjectsTable';
import DirectiveCards from '../../components/DirectiveCards';
import FieldUpdateBanner from '../../components/FieldUpdateBanner';
import AskCards from '../../components/AskCards';
import { CreatePoModal } from '../admin/CreatePoModal';
import TimeOffModal from '../TimeOffModal';
import CurrentActivityPanel from '../../components/CurrentActivity';
import PMTiles from './PMTiles';
import { MyReminders } from '../../components/Reminders';

// The PM's own dashboard — the same "at a glance" layout the Executive sees for a PM
// (GlancePillows + Work Flows + Shop + Projects table + Activity), scoped to the
// signed-in PM via a self-access on /exec/pm/:id/*, plus their priority-task cards
// and quick links. Keeps everything a PM needs; matches the modern look.
const card = { background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 12 };
const sectionLabel = { fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' };

const QUICK_LINKS = [
  { label: 'Bids',            path: '/admin/bids',            icon: '📄', color: '#1e3a5f' },
  { label: 'Projects',        path: '/admin/new-projects',    icon: '🏗️', color: '#065f46' },
  { label: 'Work Flows',      path: '/admin/workflows',       icon: '📊', color: '#a16207' },
  { label: 'Shop',            path: '/admin/shop',            icon: '🔧', color: '#4c1d95' },
  { label: 'Conversations',   path: '/admin/conversations',   icon: '💬', color: '#0e7490' },
  { label: 'Purchase Orders', path: '/admin/purchase-orders', icon: '🧾', color: '#b45309' },
];

function getSession() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null'); } catch { return null; } }

function Metric({ label, value }) {
  return (
    <div style={{ ...card, padding: '14px 16px' }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function PMDashboard() {
  const navigate = useNavigate();
  const session = getSession();
  const myId = session?.user?.id;
  const firstName = session?.user?.firstName
    || (() => { try { return JSON.parse(localStorage.getItem('te_worker') || 'null')?.firstName || ''; } catch { return ''; } })();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [favIds, setFavIds] = useState(() => new Set());
  const [usaByProject, setUsaByProject] = useState({});
  const [liveCounts, setLiveCounts] = useState({});
  const [projQuery, setProjQuery] = useState('');
  const [vendors, setVendors] = useState([]);
  const [showPo, setShowPo] = useState(false);
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  // Answering a card at the top moves it into the Bids & Banners tile — reload the tiles.
  const [tilesKey, setTilesKey] = useState(0);
  const bumpTiles = () => setTilesKey(k => k + 1);

  useEffect(() => {
    if (!myId) { setError('Could not identify your account — please sign out and back in.'); return; }
    apiFetch(`/exec/pm/${myId}/summary`).then(setData).catch(e => setError(e?.error || 'Failed to load your dashboard.'));
    apiFetch('/project-favorites').then(ids => setFavIds(new Set(Array.isArray(ids) ? ids : []))).catch(() => {});
    apiFetch('/usa-status-by-project').then(m => setUsaByProject(m && typeof m === 'object' ? m : {})).catch(() => {});
    apiFetch('/projects/live-counts').then(m => setLiveCounts(m && typeof m === 'object' ? m : {})).catch(() => {});
    apiFetch('/vendors/active').then(v => setVendors(Array.isArray(v) ? v : [])).catch(() => {});
  }, [myId]);

  async function toggleFavorite(e, pid) {
    e.stopPropagation();
    const isFav = favIds.has(pid);
    setFavIds(prev => { const n = new Set(prev); isFav ? n.delete(pid) : n.add(pid); return n; });
    try { await apiFetch(`/project-favorites/${pid}`, { method: isFav ? 'DELETE' : 'POST' }); }
    catch { setFavIds(prev => { const n = new Set(prev); isFav ? n.add(pid) : n.delete(pid); return n; }); }
  }

  const t = data?.totals || {};
  const shop = data?.shop || { new: 0, working: 0, complete: 0 };
  const projects = data?.projects || [];
  const pq = projQuery.trim().toLowerCase();
  const shownProjects = pq
    ? projects.filter(p => `${p.project_number || ''} ${p.name || ''} ${p.client_name || ''}`.toLowerCase().includes(pq))
    : projects;
  const who = firstName || (data?.pm?.name || '').split(' ')[0] || 'My';

  return (
    <AdminLayout>
      <div style={{ maxWidth: 1100 }}>
        {/* Weekly update (blue) the PM was sent and hasn't read — same card as the phone (Pat, Oct 5 2026). */}
        <FieldUpdateBanner onChange={bumpTiles} />
        {/* Priority tasks from the office — only render when there's an active one */}
        {/* Only the ones not yet seen — once "Got it" is pressed it lives in the Bids & Banners tile (Pat, Oct 7 2026). */}
        <DirectiveCards unseenOnly onChange={bumpTiles} />
        {/* Questions / RSVPs from the office — nothing renders when none are waiting */}
        <AskCards onChange={bumpTiles} />
        {/* ⏰ My Reminders (Ryan, Oct 26 2026). */}
        <div style={{ maxWidth: 640 }}><MyReminders /></div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>{error}</div>}
        {!data && !error && <div style={{ color: 'var(--text-muted)', padding: '30px 0' }}>Loading…</div>}

        {data && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#eef2ff', color: '#3730a3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 }}>
                {(data.pm?.name || who).split(' ').map(x => x[0]).slice(0, 2).join('')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{who}'s Dashboard</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Your jobs at a glance</div>
              </div>
            </div>

            {/* Activity feed + summary + Daily Update — scoped to your jobs. A Write
                PO pill sits at the far left of the row. */}
            <div style={{ marginBottom: 20 }}>
              <ActivityPills pageName={`${who}'s`} summaryLabel={`${who}'s Update`} scope="pm" scopeId={myId} endpoint={`/exec/pm/${myId}/activity`}
                leading={[{ icon: '🧾', label: 'Write PO', onClick: () => setShowPo(true) },
                  // Same request the field sends from the time clock — same approval, same schedule note.
                  { icon: '🌴', label: 'Request Time Off', onClick: () => setShowTimeOff(true) },
                  // What everyone in the office is on — each line opens the thing itself.
                  { icon: '👀', label: 'My Activity', onClick: () => setShowActivity(true) }]} />
            </div>
            {/* Stays open after Create so the green "PO Complete" card can be read; × / Done
                close it (Ryan, Sep 29 2026: "POs are leaving the screen immediately again"). */}
            <CreatePoModal open={showPo} onClose={() => setShowPo(false)} projects={projects} vendors={vendors}
              onCreated={() => {}} />
            {showTimeOff && <TimeOffModal centered onClose={() => setShowTimeOff(false)} />}
            {showActivity && <CurrentActivityPanel onClose={() => setShowActivity(false)} />}

            {/* Your Morning Brief — the podcast + rundown. Same section the Exec
                dashboard and the exec's view of a PM show, so a PM sees their own. */}
            <h2 style={sectionLabel}>🎙️ Morning Brief</h2>
            <div style={{ marginBottom: 26 }}>
              <MorningBriefs audience="pm" pmId={myId} pmName={data?.pm?.name || who} />
            </div>

            {/* Top ribbon — your numbers (expand-in-place pillows) */}
            <GlancePillows totals={t} lists={data.lists} navigate={navigate} />

            {/* The six tiles (Pat, Oct 7 2026): Bids & Banners · Bills · Updates / Workflows · POs · Submittals.
                They took the Work Flows ribbon's place. */}
            <PMTiles pmId={myId} refreshKey={tilesKey} />

            {/* Shop ribbon */}
            <h2 style={sectionLabel}>Shop</h2>
            <div onClick={() => navigate('/admin/shop')} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 30, cursor: 'pointer' }}>
              <Metric label="New requests" value={shop.new || 0} />
              <Metric label="Working on" value={shop.working || 0} />
              <Metric label="Completed" value={shop.complete || 0} />
            </div>

            {/* Your projects — same table as the Projects page (no PM column / delete) */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <h2 style={{ ...sectionLabel, margin: 0 }}>My Projects</h2>
              {projects.length > 0 && (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ position: 'absolute', left: 11, color: 'var(--text-muted)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  <input value={projQuery} onChange={e => setProjQuery(e.target.value)} placeholder="Search my projects…"
                    style={{ padding: '7px 28px 7px 32px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 999, background: 'var(--surface)', color: 'var(--text)', width: 220, boxSizing: 'border-box' }} />
                  {projQuery && <button onClick={() => setProjQuery('')} title="Clear search" style={{ position: 'absolute', right: 7, width: 20, height: 20, borderRadius: 999, border: 'none', background: '#e5e7eb', color: '#6b7280', fontSize: 13, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>}
                </div>
              )}
            </div>
            {projects.length === 0 ? (
              <div style={{ ...card, borderStyle: 'dashed', padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, marginBottom: 30 }}>No active jobs assigned to you yet.</div>
            ) : shownProjects.length === 0 ? (
              <div style={{ ...card, borderStyle: 'dashed', padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, marginBottom: 30 }}>No projects match “{projQuery}”.</div>
            ) : (
              <div className="admin-card" style={{ overflow: 'hidden', marginBottom: 30 }}>
                <ProjectsTable
                  rows={shownProjects}
                  onOpen={p => navigate(`/admin/new-projects/${p.id}`)}
                  favIds={favIds}
                  onToggleFavorite={toggleFavorite}
                  usaByProject={usaByProject}
                  liveCounts={liveCounts}
                  showPM={false}
                  canDelete={false}
                />
              </div>
            )}

            {/* Quick links */}
            <h2 style={sectionLabel}>Quick Links</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              {QUICK_LINKS.map(q => (
                <button key={q.path} onClick={() => navigate(q.path)}
                  style={{ ...card, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', borderLeft: `4px solid ${q.color}` }}>
                  <span style={{ fontSize: 20 }}>{q.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{q.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
